'use strict';

// Wrap the code in a closure in order to hide it from global scope
(() => {
  // The "oj_whenReady" global variable enables a strategy that the busy context whenReady,
  // will implicitly add a busy state, until the application calls applicationBootstrapComplete
  // on the busy state context.
  window.oj_whenReady = true;

  // The __importDefault and __importStar globals are needed for typescript code.
  // When TS transpile to AMD it generates in the js file a call to __importDefault(xxx)
  // See noEmitHelpers TSC option. With this option, TS generates calls to globals instead of
  // inserting copy of the stub function in every file.
  // eslint-disable-next-line no-underscore-dangle
  window.__importDefault = (mod) => ((mod && mod.__esModule) ? mod : { default: mod });
  // eslint-disable-next-line no-underscore-dangle
  window.__importStar = (mod) => mod;

  // The urlMarker is defined here and the Configuration class is used to access its value
  // This is the only place where the urlMarker is defined.
  const urlMarker = 'vp';

  // start injected shared scripts (src/template/calculateApplicationUrl.js, src/template/getBaseUrlFromConfig.js)
  /**
   * @param {Location} loc
   * @param {Object} [vbInitConfig = {}]
   * @param {String} [marker = 'vp']
   * @return {String}
   */
  // eslint-disable-next-line no-unused-vars
  /**
   * Based on the location and vbInitConfig parameters, calculates the application URL. The underlying assumption
   * here is that the worker (for example, sw.js) is registered at the same location as index.html.
   * Depending on the existence of the marker 'vp' in the URL, the application URL can take two values:
   *   1) If the marker is not in the URL, the application URL is the current path.
   *   2) Otherwise the application URL is the part of the URL before the marker.
   *
   * This URL can be different from the base URL used to retrieve the application resource.
   * This is will be used by the router sync navigation state and URL and service worker to cache app resources
   * Note: Do not add any references to window to this function.
   * @param {Location} loc Window.location or WorkerGlobalScope.location
   * @param {Object} [vbInitConfig = {}] application configuration
   * @param {String} [urlMarker = 'vp'] the urlMarker is defined here and the Configuration class is used to access it
   * @return {String}
   */
  const calculateApplicationUrl = (loc, vbInitConfig = {}, urlMarker = 'vp') => {
    const appName = vbInitConfig.APP_NAME;

    if (appName) {
      // Match the first occurrence of /appName/ or ending with /appName
      const regex = new RegExp(`/${appName}$|/${appName}/`, 'g');
      const result = regex.exec(loc.pathname);

      if (result) {
        let appUrl = loc.origin + loc.pathname.substring(0, result.index + 1 + appName.length);
        // Make sure to terminate with a '/'
        if (appUrl.slice(-1) !== '/') {
          appUrl += '/';
        }

        return appUrl;
      }
    }
    // Retrieve the URL without search or hash. Depending on context, pathname can be:
    // - /ic/builder/rt/rx/1.0/webApps/abc/ on the main thread
    // - /ic/builder/rt/rx/1.0/webApps/abc/sw.js in the service worker context
    // - /context.html or /debug.html for unit test
    let appUrl = loc.origin + loc.pathname;
    const visualIndex = appUrl.indexOf(`/${urlMarker}/`);

    // Make sure that we either have .../vp/... or it ends with /vp
    if (visualIndex > 0) {
      appUrl = appUrl.substring(0, visualIndex + 1);
    } else if (appUrl.substring(appUrl.length - urlMarker.length - 1) === `/${urlMarker}`) {
      appUrl = appUrl.substring(0, appUrl.length - urlMarker.length - 1);
    } else {
      // Location path might end with a resource
      // eslint-disable-next-line no-restricted-syntax
      for (const r of ['index.html', 'sw.js', 'debug.html', 'context.html']) {
        const resourceIndex = appUrl.indexOf(r);
        if (resourceIndex > 0) {
          appUrl = appUrl.substring(0, resourceIndex);
          break;
        }
      }
    }

    // Make sure to terminate with a '/'
    if (!appUrl.endsWith('/')) {
      appUrl += '/';
    }
    return appUrl;
  };

  /**
   * @param {Location} loc
   * @param {Object} vbInitConfig
   * @param {String} applicationUrl
   * @return {String}
   */
  // eslint-disable-next-line no-unused-vars
  /**
   * The base URL is where all the application resources are loaded from.
   * Note: Do not add any references to window to this function.
   * @param {Location} loc Window.location or WorkerGlobalScope.location
   * @param {Object} vbInitConfig application configuration
   * @param {String} applicationUrl calculated application URL
   * @return {String}  the baseUrl
   */
  const getBaseUrlFromConfig = (loc, vbInitConfig, applicationUrl) => {
    // If BASE_URL is defined in vbInitConfig, we use it as-is.
    let baseUrl = vbInitConfig.BASE_URL;
    // If not defined, the base URL is a combination of application URL and BASE_URL_TOKEN
    if (!baseUrl) {
      baseUrl = applicationUrl;
      const baseUrlToken = vbInitConfig.BASE_URL_TOKEN;
      if (baseUrlToken) {
        // When using vanity URL, BASE_URL_TOKEN starts with '/', which is relative to the domain.
        if (baseUrlToken[0] === '/') {
          // Note: prepend the origin due to existing apps relying on the full absolute url
          baseUrl = `${loc.origin}${baseUrlToken}`;
        } else {
          baseUrl = `${baseUrl}${baseUrlToken}`;
        }
      }
    }
    // Make sure to terminate with a '/', because BASE_URL or BASE_URL_TOKEN might not be
    if (!baseUrl.endsWith('/')) {
      baseUrl += '/';
    }
    return baseUrl;
  };

  // end injected shared scripts

  /**
   * Parse the document for script tags and retrieve info for visual-runtime.js and require.js
   * This function retrieves all the info doing only pass on the array of scripts, also stops the
   * traversal as soon as the needed info is retrieved.
   *
   * @return {{isBundled: boolean, scriptRoot: string, isRequireUt: boolean, noBootstrap: boolean}}
   * isBundled: true when using visual-runtime.js is loaded
   * scriptRoot: the path to visual-runtime.js
   * isRequireUt: true when using require-ut instead of require
   * noBootstrap: true when RT should not be bootstrapped explicitly
   */
  function getScriptInfo() {
    // Look up the root location from where this script is loaded which is used to construct the url for
    // loading the vbcs-icon-font.css.
    let noBootstrap = false;
    let isRequireUt = false;
    let scriptRoot = '';

    let doneWithVB = false;
    let doneWithRequire = false;
    const { scripts } = document;

    // Traverse collection of script once and retrieve needed info
    // Stop iterating as soon as needed info is retrieved
    for (let i = 0; i < scripts.length && !(doneWithVB && doneWithRequire); i += 1) {
      const script = scripts[i];
      const { src } = script;
      if (src) {
        // Look for visual-runtime because it could also be visual-runtime-debug
        const scriptIndex = src.indexOf('visual-runtime');
        if (scriptIndex >= 0) {
          scriptRoot = src.substring(0, scriptIndex);
          if (script.dataset.nobootstrap !== undefined) {
            noBootstrap = true;
          }
          doneWithVB = true;
        } else if (src.includes('require.js')) {
          isRequireUt = false;
          doneWithRequire = true;
        } else if (src.includes('require-ut.js')) {
          isRequireUt = true;
          doneWithRequire = true;
        }
      }
    }

    return {
      isBundled: scriptRoot !== '',
      scriptRoot,
      isRequireUt,
      noBootstrap,
    };
  }

  /**
   * Require the library to communicate with DT when an application runs inside DT.
   * This needs to be done before other RT libraries in order for page preview to work in DT
   * @param {function} initRt a callback to invoke Runtime initialization
   */
  function initRtDtEnv(initRt) {
    // Require the library to communicate with DT when an application runs inside DT.
    // This needs to be done before other library for page preview to work
    requirejs(['vb/private/rtEnvConfig'], (RtEnvConfig) => {
      const rtModule = RtEnvConfig && new RtEnvConfig().module;
      if (rtModule) {
        // This is when running in DT environment
        requirejs([rtModule], initRt);
      } else {
        initRt();
      }
    });
  }

  function installServiceWorker(vbInitConfig, Utils, config, Configuration, root) {
    // skip install service worker if skipInstall is set to true
    if (vbInitConfig.SERVICE_WORKER_CONFIG && vbInitConfig.SERVICE_WORKER_CONFIG.skipInstall) {
      return Promise.resolve();
    }
    return Utils.getResource('vb/private/serviceWorker/serviceWorkerManagerFactory')
      .then((serviceWorkerManagerFactory) => serviceWorkerManagerFactory.createServiceWorkerManager(vbInitConfig))
      .then((swm) => {
        const swmInstance = swm.getInstance();
        return swmInstance.install(root, config, Configuration);
      });
  }

  function init(applicationUrl, scriptInfo, initConfig, initParams, resolve, reject) {
    // Init RtDt environment first then continue with RT initialization
    initRtDtEnv(() => {
      if (navigator.webdriver && scriptInfo.isBundled) {
        // for security reason, we'll only enable loading of vb-test-utils.js if the application has
        // vbInitConfig.TEST_MODE set to webdriver
        requirejs.config({
          paths: {
            vbtu: `${scriptInfo.scriptRoot}vb-test-utils`,
            // needed for webdriver test to access chai in release mode grunt test-ui-release
            chai: `${scriptInfo.scriptRoot}vb-test-utils`,
          },
          bundles: {
            vbtu: ['vbtu/testUtils'],
          },
        });
      }

      requirejs([
        'vb/private/vx/registryConfig',
        'vb/private/configuration',
        'vbc/private/utils',
        'vb/private/stateManagement/routerUtils'],
      (RegistryConfig, Configuration, Utils, RouterUtils) => {
        // Restores special query params if they exist in the session storage
        // This is for preview mode and FA test
        RouterUtils.specialSearchParamsHandling();

        // Constructing the RegistryConfig will initiate the extension manager registry loading
        // so it needs to be done first
        const registryConfig = new RegistryConfig(window.vbInitConfig);

        Configuration.init({
          // Hold a copy of this function for testing
          calculateApplicationUrl,

          appName: window.vbInitConfig.APP_NAME,

          // Store the URL marker
          // The urlMarker is defined here and the Configuration class is used to access its value
          urlMarker,

          // Calculate the value once
          applicationUrl,

          getBaseUrlFromConfig,

          // Check whether require-ut is used or not and set the configuration flag
          isRequireUt: scriptInfo.isRequireUt,
        });

        if (Utils.isActionChainTestMode()) {
          // when running in action chain test mode, skip bootstrapping the application
          console.log('Starting VB in action chain test mode.');

          // send a message to the action chain tester to start running the test
          window.postMessage({
            type: 'vbActionChainTesterHandShake',
          }, window.location.origin);

          return;
        }

        // Load the configLoader later because it has dependencies on larger libraries like JET
        Utils.getResource('vb/private/configLoader')
          .then((ConfigLoader) => ConfigLoader.init(registryConfig, initConfig, initParams))
          .then((config) => installServiceWorker(
            window.vbInitConfig,
            Utils,
            config,
            Configuration,
            scriptInfo.scriptRoot))
          .then(() => Utils.getResource('vb/bootstrap'))
          .then((BootStrap) => BootStrap())
          .then(resolve)
          .then(() => {
            if (Utils.isUnitTestMode()) {
              // when running in unit test mode, we need to notify karma to start the tests after bootstrap
              console.log('Starting VB in unit test mode.');

              // send a message to karma to start running tests
              window.postMessage({
                type: 'vbUnitTestHandShake',
              }, window.location.origin);
            }
          })
          .catch(reject);
      });
    });
  }

  /**
   * Return true if we are in the hidden iframe for refreshing the access token.
   *
   * @returns {boolean}
   */
  function inRefreshAuthTokenIframe() {
    try {
      return window.self !== window.top && window.frameElement.id === 'vb-refresh-access-token-iframe';
    } catch (e) { // eslint-disable-line no-unused-vars
      return false;
    }
  }

  /**
   * Post the access token which should be attached to the URL as a hash back to the parent window.
   */
  function refreshAuthToken() {
    window.parent.postMessage({
      method: 'vbRefreshAuthToken',
      args: [window.location.hash],
    }, window.location.origin);

    if (window.location.hash) {
      // This iframe exits before Router cleanup, so clear the token fragment here after sending it to the parent.
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    }
  }

  /**
   * Returns true if vbrtAuthTokenMode query parameter is on the application url which means this is a
   * redirect back from the token relay backend after the OAuth2 code grant flow is completed
   *
   * @returns {boolean}
   */
  function isOAuth2CodeGrantFlowMode() {
    return !!new URL(window.location.href).searchParams.get('vbrtAuthTokenMode');
  }

  /**
   * Notify the initiator window of the OAuth2 code grant flow that the flow is completed.
   */
  function notifyOAuth2CodeGrantFlowCompletion() {
    const url = new URL(window.location.href);
    // Propagate the state returned by the OAuth redirect so the initiating window can validate
    // VB-RT-owned code-grant flows. External flows, such as TRAP token relay, own their state
    // validation and may use this message only as a completion signal.
    const state = url.searchParams.get('state') || new URLSearchParams(url.hash.slice(1)).get('state');

    window.opener.postMessage({
      method: 'vbOAuth2CodeGrantFlowCompleted',
      args: [state],
    }, window.location.origin);

    // persistent flag to keep third-party login window from closing for debugging purpose
    const keepOpen = window.vbInitConfig
      && window.vbInitConfig.DEBUG
      && localStorage.getItem('vbOAuth2KeepWindowOpen');
    if (!keepOpen) {
      window.close();
    }
  }

  if (isOAuth2CodeGrantFlowMode()) {
    notifyOAuth2CodeGrantFlowCompletion();
  } else if (inRefreshAuthTokenIframe()) {
    refreshAuthToken();
  } else {
    const scriptInfo = getScriptInfo();

    // Used to stop re-entry of the bootstrap function
    let alreadyStarted = false;

    /**
     * The bootstrap function for VB.
     * @global
     * @param {Object} [initParams]
     * @param {Object} [initConfig]
     * @returns {Promise} a promise that resolve when VB is started
     */
    const start = (initParams, initConfig) => Promise.resolve().then(() => {
      // Start cannot not be called multiple time
      if (alreadyStarted) {
        return undefined;
      }

      alreadyStarted = true;

      // Argument values have precedence over the content of the global object
      // Also guarantee both values are at least an empty object
      window.vbInitParams = Object.assign({}, window.vbInitParams, initParams);
      window.vbInitConfig = Object.assign({}, window.vbInitConfig, initConfig);

      const applicationUrl = calculateApplicationUrl(window.location, window.vbInitConfig, urlMarker);

      // Since we set require baseUrl, this value can later be retrieved by calling requirejs.toUrl('')
      // Don't set the baseUrl when running in action chain or unit test mode since the baseUrl should be
      // set by the test runner.
      if (window.vbInitConfig.TEST_MODE !== 'actionChain' && window.vbInitConfig.TEST_MODE !== 'unitTest') {
        // It is possible that window.vbInitConfig is not defined and that requirejs has
        // already been configured, in that case, skip the configuration
        if (Object.keys(window.vbInitConfig).length > 0) {
          requirejs.config({
            baseUrl: getBaseUrlFromConfig(window.location, window.vbInitConfig, applicationUrl),
          });
        }
      }

      return new Promise((resolve, reject) => {
        requirejs(['vb/private/vbRequireConfig'],
          (VbRequireConfig) => {
            try {
              // @@libDir and @@srcDir are injected by replace.js Grunt task
              VbRequireConfig.config(undefined, undefined, scriptInfo.scriptRoot);

              // Pass the original initConfig and initParams from the start call
              init(applicationUrl, scriptInfo, initConfig, initParams, resolve, reject);
            } catch (error) {
              reject(error);
            }
          },
          (error) => reject(error));
      });
    });

    if (scriptInfo.noBootstrap) {
      // Export the start function on the vb global
      window.vb = { start };
      define('vb/main', [], () => ({ start }));
    } else {
      // Bootstrap only in auto-boot mode
      start().catch((error) => {
        console.error('Error starting VB application', error);
      });
    }
  }
})();

